import { useNavigate, useParams } from 'react-router';

import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';

import { useI18n } from '@app-velocity/i18n';

import {
  AppraisalContext,
  SinglePageReviews,
} from '@app-velocity/modules/appraisals';

import { ErrorWithDetails, SimpleStatus } from '@app-velocity/common';

import { TNewAppraisalFlowRequest } from '@app-velocity/av-api-v2';

import { ConvertedImageRaw } from '../../../../core/types';

import {
  getPageAndFileData,
  getFullNodeForNodeId,
  getReviewNodeImage,
  getImageWithoutTextForNodeId,
  getPageStructure,
  selectNode,
} from '../../../../core';
import { PageStatusHeader } from '../../../components/PageStatusHeader';
import { bufferToBase64 } from '../../../utils/image';
import { useNewReviewContext } from '../context/useNewReviewContext';

const isCorrectImageResponse = (
  imageResponse: unknown
): imageResponse is ConvertedImageRaw => {
  if (typeof imageResponse === 'string') return false;
  const correctResponse = imageResponse as ConvertedImageRaw;
  if (imageResponse && 'converted' in correctResponse) return true;

  return false;
};

export const NewReview = () => {
  const { t } = useI18n();

  const navigate = useNavigate();

  const [node, setNode] = useState<{ name: string; id: string }>();
  const [reviewStatus, setReviewStatus] = useState<SimpleStatus | null>(null);
  const [image, setImage] = useState<string>('');
  const [reviewError, setReviewError] = useState<ErrorWithDetails | Error>();

  const { onReviewStart, goToAppraisalDetails, workspaceId, teamId } =
    useNewReviewContext();

  const { nodeId, method, level } = useParams<{
    nodeId: string;
    method: string;
    level?: string;
  }>();

  const getNode = async () => {
    if (!nodeId) return;
    const fullNode = await getFullNodeForNodeId(nodeId, 99);

    if (!fullNode) return;
    setNode(fullNode);
  };

  const getReviewMethod = (): TNewAppraisalFlowRequest['method'] => {
    switch (method) {
      case 'accessibility':
        return 'ACCESSIBILITY';
      case 'refactoringui':
        return 'REFACTORING_UI';
      default:
        return 'ACCESSIBILITY';
    }
  };

  const readAppraisals = async (nodeId: string) => {
    try {
      const backgroundScreenShot = await getImageWithoutTextForNodeId(nodeId);
      if (!backgroundScreenShot) return;
      const pageImage = await getReviewNodeImage(nodeId, undefined, false);
      if (!isCorrectImageResponse(pageImage)) return;
      const base64Image = pageImage.converted
        ? await bufferToBase64(pageImage.converted)
        : '';
      pageImage.converted = null;
      setImage(base64Image);

      const backgroundColorBase64 = await bufferToBase64(backgroundScreenShot);
      const pageStructure = await getPageStructure(nodeId);

      if (!pageStructure || !onReviewStart) return;

      const { pageName, fileId } = await getPageAndFileData();

      const startedReviewDelegatedJob = await onReviewStart({
        pageStructure,
        pageImage: base64Image,
        level,
        method: getReviewMethod(),
        backgroundImageData: backgroundColorBase64,
        nodeId,
        nodeName: node?.name || '',
        sourceId: fileId,
        pageName,
      });

      if (startedReviewDelegatedJob?.entityId && goToAppraisalDetails)
        navigate(
          goToAppraisalDetails(
            teamId,
            workspaceId,
            startedReviewDelegatedJob?.entityId
          )
        );
    } catch (e) {
      const correctError: AxiosError | Error | ErrorWithDetails = e as
        | AxiosError
        | Error
        | ErrorWithDetails;
      Sentry.captureException(e);
      setReviewError(correctError);
      setReviewStatus('error');
    }
  };

  useEffect(() => {
    if (!nodeId) return;
    getNode();
  }, [nodeId]);

  useEffect(() => {
    if (!node) return;

    setReviewStatus('pending');
    readAppraisals(node.id);
  }, [node]);

  const goBack = () => {
    navigate('..');
  };

  const getCategoryByMethod = () => {
    switch (method) {
      case 'accessibility':
        return 'ACCESSIBILITY';
      case 'refactoringui':
        return 'REFACTORING_UI';
      default:
        return null;
    }
  };

  const getTitleByMethod = (reviewLevel?: string): string => {
    switch (method) {
      case 'accessibility':
        return t('appraisals:design_review_accessibility', {
          level: reviewLevel,
        });
      case 'refactoringui':
        return t('appraisals:design_review_refactoringui');
    }

    return t('appraisals:design_review');
  };

  const navigateToElement = (elementNodeId: string) => {
    selectNode(elementNodeId);
  };

  return (
    <>
      <PageStatusHeader
        label={getTitleByMethod(level)}
        description={node?.name || ''}
        status={reviewStatus}
        onBack={goBack}
      />
      <AppraisalContext.Provider
        value={{
          status: 'pending',
          reviews: [],
          reviewError,
          nodeName: node?.name || '',
          nodeId: node?.id || '',
          image,
          category: getCategoryByMethod(),
          navigateToElement,
        }}
      >
        <SinglePageReviews />
      </AppraisalContext.Provider>
    </>
  );
};
